// LOGIC PORTED VERBATIM from LC2's commons/components/Offers/OffersMainComponent.js.
// Same computation: flatten `campaign.all_actions` (skipping legacy actions and offers with
// no reward_amount) through `formatOtherOffers`, then bucket the results into
// past/current/future by comparing each offer's start/end date against "today"
// (isSameOrAfter/isSameOrBefore, same as LC2), sorting each bucket and assigning colors from
// offersConstants (past -> pastOffersColors[0], current -> round-robin over
// activeOffersColors, future -> upcomingOffersColors[0]). No React.lazy/Loadable wrapper here
// since neither child pulls in a heavy dependency (LC2 lazy-loaded these mainly because the
// same directory's create/edit/delete offer modals are heavy; those modals are out of scope
// for this port, see OffersPlanner.js).
import React, {Fragment, useMemo} from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

import {formatOtherOffers} from './utils';
import {activeOffersColors, pastOffersColors, upcomingOffersColors} from '../../../commons/constants/offersConstants';

import OfferTimeline from './OffersTimeline';
import OfferPlanner from './OffersPlanner';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const OffersMainComponent = ({campaign}) => {
    const offers = useMemo(() => {
        if (campaign) {
            const formattedOtherOffers = [];
            const newActions = [...(campaign.all_actions || [])].filter((ele) => !ele.id.includes('legacy-action'));

            for (const ele of newActions) {
                const isLegacy = !('goal_id' in ele);
                const actionOffers = [...(ele.offers || [])]
                    .filter((e) => e.reward_amount)
                    .map((e) => ({...e, action_name: ele.name, isLegacy, campaign_status: campaign.status}));
                const formattedActionOffers = [...actionOffers].map((e) => formatOtherOffers(e)).filter((e) => e);
                formattedOtherOffers.push(...formattedActionOffers);
            }

            return [...formattedOtherOffers];
        }
        return [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify({...campaign})]);

    const offersObj = useMemo(() => {
        const pastOffers = [];
        const currentOffers = [];
        const futureOffers = [];

        for (const offer of offers) {
            const startDate = dayjs(offer.startDate).startOf('day');
            const endDate = dayjs(offer.endDate).endOf('day');
            const today = dayjs();

            if (startDate && endDate) {
                if (today.isSameOrAfter(startDate) && today.isSameOrBefore(endDate)) {
                    currentOffers.push(offer);
                } else if (today.isBefore(startDate)) {
                    futureOffers.push(offer);
                } else {
                    pastOffers.push(offer);
                }
            } else if (startDate) {
                if (today.isSameOrAfter(startDate)) {
                    currentOffers.push(offer);
                } else if (today.isBefore(startDate)) {
                    futureOffers.push(offer);
                }
            }
        }

        const sortedPastOffers = [...pastOffers]
            .sort((a, b) => b.startDate - a.startDate)
            .map((ele) => ({...ele, color: pastOffersColors[0]}));
        const sortedCurrentOffers = [...currentOffers]
            .sort((a, b) => b.startDate - a.startDate)
            .map((ele, idx) => ({...ele, color: activeOffersColors[idx % activeOffersColors.length]}));
        const sortedFutureOffers = [...futureOffers]
            .sort((a, b) => a.startDate - b.startDate)
            .map((ele) => ({...ele, color: upcomingOffersColors[0]}));

        return {
            pastOffers: sortedPastOffers,
            currentOffers: sortedCurrentOffers,
            futureOffers: sortedFutureOffers
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(offers)]);

    return (
        <Fragment>
            <OfferTimeline offersObj={offersObj} campaign={campaign} />
            <OfferPlanner offersObj={offersObj} campaign={campaign} />
        </Fragment>
    );
};

OffersMainComponent.propTypes = {
    campaign: PropTypes.object
};

export default OffersMainComponent;
